/* ==========================================================================
   Blenders Online 2026 · bo-explainer.js · "Household or commercial" (sections/bo-explainer.liquid)
   Switches the section into its scroll view on computers with motion allowed (.bo-ex--live), then keeps
   the current point in step with the scroll: the point that crosses the middle of the screen is "on", its
   photo fills the frame (the CSS does the fill; where the browser has scroll-driven animations the CSS
   follows the scroll by itself and these classes only drive the switch, the dots and the words), the
   switch slides to its side. Reduced motion: nothing happens, the plain list stays. Phones: their swipe row
   (02-10) is CSS plus bo-main.js's rail ([data-bo-rail] on .bo-ex__points); here only the keyboard's way in.
   Deferred, scoped, idempotent; re-binds on shopify:section:load, lets go on unload.
   ========================================================================== */
(() => {
  'use strict';
  if (window.BOExplainer) return;
  const live = window.matchMedia('(min-width: 900px) and (min-height: 560px) and (prefers-reduced-motion: no-preference)');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const roots = new Map();

  // Phones (02-10): the cards are one row you swipe (bo-explainer.css 3). Tabbing to the link of a card that only
  // peeks in, the browser moves the row by the smallest step and the row's snap pulls it straight back, so the
  // focused card stayed out of sight. A frame later (after the browser's own move) the row brings that whole card
  // to its start, a snap position, so it stays there. Only the row moves, never the page.
  // Keyboard focus only (:focus-visible, review 02-10): a mouse press focuses the link on the way DOWN, so moving the
  // row slid the link out from under the pointer before the release; the click landed on the card and the page never
  // opened (a computer window under 750px wide). A finger's tap is not :focus-visible either and needs no help.
  function onFocus(e) {
    try { if (!e.target.matches(':focus-visible')) return; } catch (err) { /* old browser: treat as keyboard */ }
    const card = e.target.closest && e.target.closest('.bo-ex__point');
    const track = card && card.parentElement;
    if (!track || !track.matches('.bo-ex__track') || track.scrollWidth <= track.clientWidth + 1) return;
    const pad = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
    requestAnimationFrame(() => track.scrollTo({ left: card.offsetLeft - pad, behavior: reduce.matches ? 'auto' : 'smooth' }));
  }

  function setActive(st, i) {
    if (i === st.active) return;
    st.active = i;
    st.points.forEach((p, k) => p.classList.toggle('is-on', k === i));
    st.shots.forEach((s, k) => { s.classList.toggle('is-on', k <= i); s.classList.toggle('is-current', k === i); });
    st.dots.forEach((d, k) => d.classList.toggle('is-on', k === i));
    const side = st.points[i] && st.points[i].dataset.side;
    if (side) st.root.dataset.side = side;
  }

  function start(st) {
    if (st.io) return;
    st.root.classList.add('bo-ex--live');
    st.active = -1;
    setActive(st, 0);
    // a thin line across the middle of the screen: the point on it is the current one
    st.io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) setActive(st, st.points.indexOf(en.target)); });
    }, { rootMargin: '-50% 0px -50% 0px' });
    st.points.forEach((p) => st.io.observe(p));
  }

  function stop(st) {
    if (st.io) { st.io.disconnect(); st.io = null; }
    st.root.classList.remove('bo-ex--live');
    st.points.forEach((p) => p.classList.remove('is-on'));
  }

  function sync(st) { if (live.matches && st.points.length > 1) start(st); else stop(st); }

  function bind(root) {
    if (roots.has(root)) return;
    const st = {
      root,
      points: Array.from(root.querySelectorAll('.bo-ex__point')),
      shots: Array.from(root.querySelectorAll('.bo-ex__shot')),
      dots: Array.from(root.querySelectorAll('.bo-ex__dots > i')),
      active: -1,
      io: null
    };
    roots.set(root, st);
    root.addEventListener('focusin', onFocus);
    sync(st);
  }

  function release(root) {
    const st = roots.get(root);
    if (!st) return;
    stop(st);
    root.removeEventListener('focusin', onFocus);
    roots.delete(root);
  }

  const init = (scope) => (scope || document).querySelectorAll('[data-bo-ex]').forEach(bind);
  const onChange = () => roots.forEach(sync);
  if (live.addEventListener) live.addEventListener('change', onChange); else live.addListener(onChange);

  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => e.target.querySelectorAll('[data-bo-ex]').forEach(release));
  // theme editor: choosing a point in the sidebar shows it
  document.addEventListener('shopify:block:select', (e) => {
    const point = e.target.closest && e.target.closest('.bo-ex__point');
    const root = point && point.closest('[data-bo-ex]');
    const st = root && roots.get(root);
    if (!st) return;
    if (st.io) setActive(st, st.points.indexOf(point));
    point.scrollIntoView({ block: 'center', behavior: live.matches ? 'smooth' : 'auto' });
  });

  window.BOExplainer = { init, release };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
})();
